const VLAN_INTERFACE_PATTERN = /^(eth[01])\.(\d+)$/;

export const normalizeInterfaceKey = (value) => {
  const normalized =
    value === undefined || value === null
      ? ""
      : String(value).trim().toLowerCase();

  if (!normalized) return "";
  if (VLAN_INTERFACE_PATTERN.test(normalized)) return normalized;
  if (
    normalized.includes("ppp") ||
    normalized.includes("lte") ||
    normalized.includes("wwan") ||
    normalized.includes("cellular")
  ) {
    return "lte0";
  }
  if (normalized.includes("wlan") || normalized.includes("wifi")) {
    return "wlan0";
  }
  if (normalized === "eth1") {
    return "eth1";
  }
  if (normalized === "br0" || normalized === "eth0" || normalized === "eth") {
    return "eth0";
  }
  return normalized;
};

export const isLteInterface = (value) => normalizeInterfaceKey(value) === "lte0";

const isVlanInterface = (value) => VLAN_INTERFACE_PATTERN.test(normalizeInterfaceKey(value));

const formatVlanLabel = (value) => {
  const match = normalizeInterfaceKey(value).match(VLAN_INTERFACE_PATTERN);
  return match ? `VLAN ${match[2]}` : "";
};

const safeConf = (conf) =>
  conf && typeof conf === "object" && !Array.isArray(conf) ? conf : {};

const hasOwnProperty = (object, property) =>
  Object.prototype.hasOwnProperty.call(object, property);

const isEnabledFlag = (value) => {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return ["true", "1", "enabled", "yes"].includes(normalized);
  }
  return Boolean(value);
};

const valueOrEmpty = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const hasCellularConfig = (conf) => hasOwnProperty(conf, "enableCellular");

const isCellularEnabled = (conf) =>
  hasCellularConfig(conf) && isEnabledFlag(conf.enableCellular);

const hasWifiConfig = (conf = {}, device = {}, networkStatus = null) => {
  const firmwareText = [
    conf.controllerFirmwareVersion,
    conf.firmwareVersion,
    device.controllerFirmwareVersion,
    device.firmwareVersion,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return Boolean(
    networkStatus?.features?.wifi?.available ||
      conf.wifiInterface ||
      conf.wifiMacAddress ||
      conf.wifiMac ||
      conf.wifiStatus ||
      valueOrEmpty(conf.wifiSSID) ||
      firmwareText.includes("wifi")
  );
};

const compactJoin = (parts) =>
  parts
    .map((part) => valueOrEmpty(part))
    .filter(Boolean)
    .join(" · ");

const formatAddressMode = (value) => {
  const mode = valueOrEmpty(value).toLowerCase();
  if (mode === "dhcp") return "DHCP";
  if (mode === "static") return "Static";
  return "";
};

const normalizeLinkStatusText = (value) => valueOrEmpty(value).replace(/^"+|"+$/g, "");

const isFrontEthernetInterface = (value) => {
  const key = normalizeInterfaceKey(value);
  return key === "eth0";
};

const isSecondaryInterfaceModeEnabled = (conf = {}) => {
  const cfg = safeConf(conf);
  return (
    cfg.secondaryNetworkInterfaceMode === "secondary_nic" ||
    cfg.secondaryNetworkInterfaceMode === "bridge_mode"
  );
};

const getSecondaryInterfaceRole = (conf = {}) => {
  const cfg = safeConf(conf);
  if (cfg.secondaryNetworkInterfaceMode === "bridge_mode") return "Bridge member";
  if (cfg.secondaryNetworkInterfaceMode === "secondary_nic") return "Secondary";
  return "USB Ethernet";
};

const getConfiguredDefaultRoute = (conf) =>
  conf.currentDefaultRoute || conf.defaultNetworkRouteInterface || "";

export const interfacesMatch = (left, right) => {
  const leftKey = normalizeInterfaceKey(left);
  const rightKey = normalizeInterfaceKey(right);
  if (!leftKey || !rightKey) return false;
  return leftKey === rightKey;
};

export const formatInterfaceLabel = (value) => {
  const key = normalizeInterfaceKey(value);
  if (key === "lte0") return "LTE (lte0)";
  if (key === "wlan0") return "WiFi";
  if (key === "eth1") return "USB Ethernet";
  if (key === "eth0") return "Front Ethernet";
  if (isVlanInterface(key)) return formatVlanLabel(key);
  return value ? String(value) : "Unknown";
};

export const resolvePrimaryEthernetInterface = (conf = {}) => {
  const cfg = safeConf(conf);
  if (cfg.primaryInterface) {
    return normalizeInterfaceKey(cfg.primaryInterface);
  }
  return "eth0";
};

export const resolveWanPrimaryInterface = (conf = {}) => {
  const cfg = safeConf(conf);
  if (isCellularEnabled(cfg) && cfg.ltePolicy === "lteOnly") {
    return "lte0";
  }
  return resolvePrimaryEthernetInterface(cfg);
};

export const resolveSecondaryInterface = (conf = {}) => {
  const cfg = safeConf(conf);
  if (
    cfg.secondaryNetworkInterfaceMode === "secondary_nic" ||
    cfg.secondaryNetworkInterfaceMode === "bridge_mode" ||
    cfg.secondaryIpAssign ||
    cfg.secondaryDhcpAssignedIp ||
    cfg.secondaryStaticIp ||
    cfg.secondInterfaceStatus
  ) {
    return "eth1";
  }
  return "";
};

const resolveAutopingPrimaryInterface = (conf = {}) => {
  const cfg = safeConf(conf);
  if (cfg.primaryInterface) {
    return String(cfg.primaryInterface).trim();
  }
  if (cfg.secondaryNetworkInterfaceMode === "bridge_mode") {
    return "br0";
  }
  return "eth0";
};

const resolveAutopingSecondaryInterface = (conf = {}) => {
  const cfg = safeConf(conf);
  if (
    cfg.secondaryNetworkInterfaceMode === "secondary_nic" ||
    cfg.secondaryNetworkInterfaceMode === "bridge_mode"
  ) {
    return "eth1";
  }
  if (cfg.secondaryInterface) {
    return String(cfg.secondaryInterface).trim();
  }
  return "";
};

const resolveAutopingWifiInterface = (conf = {}) =>
  valueOrEmpty(conf.wifiInterface) || "wlan0";

const resolveAutopingCellularInterface = (conf = {}) =>
  valueOrEmpty(conf.cellularInterface) || "ppp0";

const pushUniqueOption = (options, seen, value, label) => {
  const normalizedValue = valueOrEmpty(value);
  if (!normalizedValue || seen.has(normalizedValue)) return;
  options.push({ value: normalizedValue, label });
  seen.add(normalizedValue);
};

const formatAutopingInterfaceOptionLabel = (value, conf = {}) => {
  const cfg = safeConf(conf);
  const rawValue = valueOrEmpty(value);
  const normalizedRaw = rawValue.toLowerCase();
  const primaryInterface = resolveAutopingPrimaryInterface(cfg);
  const secondaryInterface = resolveAutopingSecondaryInterface(cfg);

  if (!rawValue || normalizedRaw === "default") {
    return `Default (${formatInterfaceLabel(primaryInterface)})`;
  }
  if (normalizedRaw === "1") return "Primary (legacy value 1)";
  if (normalizedRaw === "2") return "Secondary (legacy value 2)";
  if (normalizedRaw === "primary") {
    return `Primary (${formatInterfaceLabel(primaryInterface)})`;
  }
  if (normalizedRaw === "secondary") {
    return secondaryInterface
      ? `Secondary (${formatInterfaceLabel(secondaryInterface)})`
      : "Secondary";
  }
  if (normalizedRaw === "lte") return formatInterfaceLabel("lte0");
  if (normalizedRaw === "wifi") return formatInterfaceLabel("wlan0");
  return formatInterfaceLabel(rawValue);
};

const extractAutopingVlanOptions = (conf = {}) => {
  const cfg = safeConf(conf);
  const rawVlans = Array.isArray(cfg.pduVlans)
    ? cfg.pduVlans
    : Array.isArray(cfg.pdu_vlans)
    ? cfg.pdu_vlans
    : [];

  return rawVlans
    .map((entry = {}) => {
      const interfaceName =
        entry.interface ||
        entry.ifname ||
        (entry.parent && entry.vid ? `${entry.parent}.${entry.vid}` : "");
      if (!interfaceName) return null;
      const vid =
        entry.vid ||
        entry.vlan ||
        (String(interfaceName).includes(".")
          ? String(interfaceName).split(".").pop()
          : "");
      return {
        value: interfaceName,
        label: vid ? `VLAN ${vid} (${interfaceName})` : interfaceName,
      };
    })
    .filter(Boolean);
};

export const getNetworkInterfaceOptions = (
  conf = {},
  { ensureValues = [], device = {}, networkStatus = null } = {},
) => {
  const cfg = safeConf(conf);
  const options = [];
  const seen = new Set();
  const primaryInterface = resolveAutopingPrimaryInterface(cfg);
  const secondaryInterface = resolveAutopingSecondaryInterface(cfg);

  pushUniqueOption(
    options,
    seen,
    "default",
    `Default (${formatInterfaceLabel(primaryInterface)})`,
  );
  pushUniqueOption(
    options,
    seen,
    primaryInterface,
    `Primary (${formatInterfaceLabel(primaryInterface)})`,
  );

  if (secondaryInterface) {
    pushUniqueOption(
      options,
      seen,
      secondaryInterface,
      `Secondary (${formatInterfaceLabel(secondaryInterface)})`,
    );
  }

  if (hasWifiConfig(cfg, device, networkStatus)) {
    pushUniqueOption(
      options,
      seen,
      resolveAutopingWifiInterface(cfg),
      formatInterfaceLabel("wlan0"),
    );
  }

  if (hasCellularConfig(cfg)) {
    pushUniqueOption(
      options,
      seen,
      resolveAutopingCellularInterface(cfg),
      formatInterfaceLabel("lte0"),
    );
  }

  extractAutopingVlanOptions(cfg).forEach((option) => {
    pushUniqueOption(options, seen, option.value, option.label);
  });

  ensureValues
    .filter((value) => value !== undefined && value !== null && value !== "")
    .forEach((value) => {
      const normalizedValue = String(value).trim();
      if (seen.has(normalizedValue)) return;
      pushUniqueOption(
        options,
        seen,
        normalizedValue,
        formatAutopingInterfaceOptionLabel(normalizedValue, cfg),
      );
    });

  return options;
};

export const getNetworkInterfaceLabel = (rawValue, conf = {}) => {
  const value =
    rawValue === undefined || rawValue === null || rawValue === ""
      ? "default"
      : String(rawValue).trim();
  const option = getNetworkInterfaceOptions(conf, {
    ensureValues: [value],
  }).find((candidate) => candidate.value === value);
  return option ? option.label : formatAutopingInterfaceOptionLabel(value, conf);
};

export const getPrimaryIp = (conf = {}) => {
  const cfg = safeConf(conf);
  if (cfg.ipAssign === "static" && cfg.staticIp) {
    return cfg.staticIp;
  }
  if (cfg.ipAssign === "dhcp" && cfg.dhcpAssignedIp) {
    return cfg.dhcpAssignedIp;
  }
  return cfg.dhcpAssignedIp || cfg.staticIp || "";
};

const getVlanConfigForInterface = (conf = {}, interfaceName) => {
  const cfg = safeConf(conf);
  const key = normalizeInterfaceKey(interfaceName);
  if (!isVlanInterface(key) || !Array.isArray(cfg.pduVlans)) return null;
  return cfg.pduVlans.find((vlan) => normalizeInterfaceKey(vlan?.interface) === key) || null;
};

const stripCidr = (value) => valueOrEmpty(value).split("/")[0] || "";

const getAddressModeForInterface = (conf = {}, interfaceName) => {
  const cfg = safeConf(conf);
  const key = normalizeInterfaceKey(interfaceName);
  const vlan = getVlanConfigForInterface(cfg, key);
  if (vlan) return formatAddressMode(vlan.mode);
  if (key === "eth1") return formatAddressMode(cfg.secondaryIpAssign);
  if (key === "lte0" || key === "wlan0") return "";
  return formatAddressMode(cfg.ipAssign);
};

const getMacForInterface = (conf = {}, interfaceName, wifiStatus = {}) => {
  const cfg = safeConf(conf);
  const key = normalizeInterfaceKey(interfaceName);
  if (key === "eth1") return cfg.secondaryMacAddr || "";
  if (key === "wlan0") return wifiStatus.macAddress || "";
  if (key === "lte0") return "";
  return cfg.macAddr || "";
};

const getGatewayForInterface = (conf = {}, interfaceName, wifiStatus = {}) => {
  const cfg = safeConf(conf);
  const key = normalizeInterfaceKey(interfaceName);
  const vlan = getVlanConfigForInterface(cfg, key);
  if (vlan) return vlan.gateway || "";
  if (key === "eth1") return cfg.secondaryGatewayIp || "";
  if (key === "wlan0") return wifiStatus.gateway || "";
  if (key === "lte0") return "";
  return cfg.gatewayIp || "";
};

export const getSecondaryIp = (conf = {}) => {
  const cfg = safeConf(conf);
  if (cfg.secondaryIpAssign === "static" && cfg.secondaryStaticIp) {
    return cfg.secondaryStaticIp;
  }
  if (cfg.secondaryIpAssign === "dhcp" && cfg.secondaryDhcpAssignedIp) {
    return cfg.secondaryDhcpAssignedIp;
  }
  return cfg.secondaryDhcpAssignedIp || cfg.secondaryStaticIp || "";
};

export const getIpForInterface = (conf = {}, interfaceName, wifiStatus = {}) => {
  const cfg = safeConf(conf);
  const key = normalizeInterfaceKey(interfaceName);
  const vlan = getVlanConfigForInterface(cfg, key);
  if (vlan) return stripCidr(vlan.ipAddr || vlan.ipAddress || "");
  if (key === "lte0") return "";
  if (key === "eth1") return getSecondaryIp(cfg);
  if (key === "wlan0") return wifiStatus.ipAddress || "";
  return getPrimaryIp(cfg);
};

export const getPrimaryInterfaceSummary = (conf = {}, networkStatus = null) => {
  const cfg = safeConf(conf);
  const primary = networkStatus?.primaryInterface;
  if (primary?.label) {
    const interfaceName = normalizeInterfaceKey(primary.interfaceName);
    return {
      interfaceName,
      label: interfaceName ? formatInterfaceLabel(interfaceName) : primary.label,
      role: primary.role || "Primary",
    };
  }

  const interfaceName = resolveWanPrimaryInterface(cfg);
  return {
    interfaceName: normalizeInterfaceKey(interfaceName),
    label: formatInterfaceLabel(interfaceName),
    role: "Primary",
  };
};

export const getWanPrioritySummary = (conf = {}, networkStatus = null) => {
  const cfg = safeConf(conf);
  if (isCellularEnabled(cfg)) {
    if (cfg.ltePolicy === "lteOnly") {
      return {
        interfaceName: "lte0",
        label: "LTE primary, ethernet fallback",
        detail: "",
      };
    }
    return {
      interfaceName: normalizeInterfaceKey(resolvePrimaryEthernetInterface(cfg)),
      label: "Ethernet primary, LTE fallback",
      detail: "",
    };
  }

  if (cfg.secondaryNetworkInterfaceMode === "bridge_mode") {
    return {
      interfaceName: "eth0",
      label: "Front Ethernet",
      detail: "Primary",
    };
  }

  const activeRoute = getActiveRoute(cfg, networkStatus);
  if (activeRoute && activeRoute !== "eth0") {
    return {
      interfaceName: activeRoute,
      label: formatInterfaceLabel(activeRoute),
      detail: "Primary",
    };
  }

  const wifi = networkStatus?.wifi?.data || {};
  if (networkStatus?.features?.wifi?.available && wifi.connected) {
    return {
      interfaceName: "wlan0",
      label: "Front Ethernet",
      detail: "WiFi management connected",
    };
  }

  const interfaceName = resolveWanPrimaryInterface(cfg);
  return {
    interfaceName: normalizeInterfaceKey(interfaceName),
    label: formatInterfaceLabel(interfaceName),
    detail: "",
  };
};

const getSignalText = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const text = String(value).trim();
  if (!text) return "";
  return text.toLowerCase().includes("dbm") ? text : `${text} dBm`;
};

const getWanHealthPayload = (networkStatus = null) => {
  const payload = networkStatus?.wanHealth?.data || networkStatus?.wanHealth || {};
  return payload && typeof payload === "object" && !Array.isArray(payload)
    ? payload
    : {};
};

const getWanHealthConfig = (networkStatus = null) => {
  const payload = getWanHealthPayload(networkStatus);
  return payload.config && typeof payload.config === "object"
    ? payload.config
    : payload;
};

const getWanHealthRoutes = (networkStatus = null) => {
  const payload = getWanHealthPayload(networkStatus);
  if (Array.isArray(payload.routes)) return payload.routes;
  if (Array.isArray(payload.config?.routes)) return payload.config.routes;
  return [];
};

const getRouteInterface = (route = {}) =>
  route.interface ||
  route.interfaceName ||
  route.name ||
  route.iface ||
  route.dev ||
  "";

const findWanRouteForInterface = (interfaceName, networkStatus = null) =>
  getWanHealthRoutes(networkStatus).find((route) =>
    interfacesMatch(getRouteInterface(route), interfaceName),
  );

const getRouteHealthy = (route = null) => {
  if (!route) return null;
  if (route.healthy === true) return true;
  if (route.healthy === false) return false;
  const status = valueOrEmpty(
    route.health ||
      route.state ||
      route.status ||
      route.lastResult ||
      route.monitoringState,
  ).toLowerCase();
  if (!status) return null;
  if (status.includes("unhealthy") || status.includes("failed")) return false;
  if (status.includes("healthy") || status.includes("ok")) return true;
  return null;
};

const getRoutePriority = (route = {}) => {
  const value = Number(route.priority ?? route.metric);
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
};

const getInferredActiveWanRoute = (networkStatus = null) => {
  const routes = getWanHealthRoutes(networkStatus)
    .filter((route) => normalizeInterfaceKey(getRouteInterface(route)))
    .sort((left, right) => getRoutePriority(left) - getRoutePriority(right));
  if (!routes.length) return "";
  const healthyRoute = routes.find((route) => getRouteHealthy(route) === true);
  return normalizeInterfaceKey(getRouteInterface(healthyRoute || routes[0]));
};

const getMonitoringEnabled = (networkStatus = null) => {
  const config = getWanHealthConfig(networkStatus);
  if (config.monitoringEnabled !== undefined) {
    return Boolean(config.monitoringEnabled);
  }
  return true;
};

const getFailoverDetail = (conf = {}, interfaceName, active) => {
  const cfg = safeConf(conf);
  if (!active || !isCellularEnabled(cfg)) return "";
  if (cfg.ltePolicy === "lteFallback" && isLteInterface(interfaceName)) {
    return "Ethernet primary not active";
  }
  if (cfg.ltePolicy === "lteOnly" && isFrontEthernetInterface(interfaceName)) {
    return "LTE primary not active";
  }
  return "";
};

const getActiveRoute = (conf = {}, networkStatus = null) => {
  const cfg = safeConf(conf);
  const configuredRoute = getConfiguredDefaultRoute(safeConf(conf));
  return normalizeInterfaceKey(
    configuredRoute ||
      getInferredActiveWanRoute(networkStatus) ||
      networkStatus?.activeUplink?.interfaceName ||
      networkStatus?.activeUplink?.runtimeInterfaceName ||
      resolveWanPrimaryInterface(cfg) ||
      "",
  );
};

const isRowActive = (row = {}, conf = {}, networkStatus = null) => {
  const activeRoute = getActiveRoute(conf, networkStatus);
  if (activeRoute && interfacesMatch(row.interfaceName, activeRoute)) {
    return true;
  }
  return Boolean(row.active);
};

const getConnectivityStatus = ({
  row,
  conf,
  networkStatus,
  route,
  active,
}) => {
  const cfg = safeConf(conf);
  const key = normalizeInterfaceKey(row.interfaceName);
  const type = row.type || "";
  const routeHealthy = getRouteHealthy(route);
  const monitoringEnabled = getMonitoringEnabled(networkStatus);
  const wanHealthAvailable = Boolean(networkStatus?.features?.wanHealth?.available);

  if (key === "lte0" && hasCellularConfig(cfg) && !isCellularEnabled(cfg)) {
    return "Disabled";
  }

  if (key === "wlan0") {
    const connected =
      row.connected !== undefined
        ? Boolean(row.connected)
        : valueOrEmpty(row.health).toLowerCase().includes("connected");
    if (active && routeHealthy === false) return "Active, degraded";
    if (active) return "Active";
    return connected ? "Connected" : row.health || "Disconnected";
  }

  if (route) {
    if (active && routeHealthy === false) return "Active, degraded";
    if (active) return "Active";
    if (key === "eth1" && cfg.secondaryNetworkInterfaceMode === "secondary_nic") {
      return "Standby";
    }
    if (!monitoringEnabled) return "Not monitored";
    if (routeHealthy === true) return "Ready";
    if (routeHealthy === false) return "Unavailable";
    return wanHealthAvailable ? "Not monitored" : row.health || "Unknown";
  }

  if (active) return "Active";

  if (key === "eth1") {
    if (cfg.secondaryNetworkInterfaceMode === "bridge_mode") {
      return valueOrEmpty(row.health).toLowerCase().includes("up")
        ? "Connected"
        : "Link unknown";
    }
    const linkStatus = normalizeLinkStatusText(row.linkSignal || row.health);
    const normalizedLinkStatus = linkStatus.toLowerCase();
    if (
      normalizedLinkStatus.includes("down") ||
      normalizedLinkStatus.includes("disconnected")
    ) {
      return "Link down";
    }
    if (row.ipAddress || linkStatus.toLowerCase().includes("up")) {
      return cfg.secondaryNetworkInterfaceMode === "secondary_nic"
        ? "Standby"
        : "Connected";
    }
    return isSecondaryInterfaceModeEnabled(cfg) ? "Configured" : row.health || "Unknown";
  }

  if (key === "lte0") {
    const health = valueOrEmpty(row.health).toLowerCase();
    const detail = valueOrEmpty(row.statusDetail).toLowerCase();
    const registration = valueOrEmpty(row.registrationStatus || row.health);
    if (
      health.includes("unavailable") ||
      detail.includes("not detected") ||
      detail.includes("not responding") ||
      detail.includes("unavailable")
    ) {
      return "Unavailable";
    }
    if (health.includes("not registered")) return "Not registered";
    if (health.includes("no lte route")) return "No LTE route";
    if (registration.toLowerCase().includes("registered")) return "Ready";
    return isCellularEnabled(cfg)
      ? wanHealthAvailable
        ? "No LTE route"
        : "Not monitored"
      : "Disabled";
  }

  if (type === "ethernet" && row.ipAddress) {
    const linkStatus = normalizeLinkStatusText(row.health);
    if (linkStatus.toLowerCase().includes("down")) return "Down";
    return wanHealthAvailable ? "Configured" : "Active";
  }

  return wanHealthAvailable ? "No route" : row.health || "Not monitored";
};

const getInterfaceDetail = ({ row, conf, networkStatus, route, active }) => {
  const cfg = safeConf(conf);
  const key = normalizeInterfaceKey(row.interfaceName);
  const wifiStatus = networkStatus?.wifi?.data || {};
  const cellularStatus = networkStatus?.cellular?.data || {};
  const failoverDetail = getFailoverDetail(cfg, key, active);
  const routePriority =
    route?.priority !== undefined && route?.priority !== null
      ? `Priority ${route.priority}`
      : "";
  const lastCheck = route?.lastCheck
    ? `Last check ${new Date(Number(route.lastCheck) * 1000).toLocaleString()}`
    : "";

  if (key === "lte0") {
    const statusDetail = row.statusDetail || cellularStatus.statusDetail;
    const operator = row.operator || cellularStatus.operator;
    return compactJoin([
      failoverDetail,
      statusDetail,
      operator && operator !== statusDetail ? operator : "",
      getSignalText(row.signalStrength ?? cellularStatus.signalStrength),
      row.registrationStatus || cellularStatus.registrationStatus,
      routePriority,
      lastCheck,
    ]);
  }

  if (key === "wlan0") {
    return compactJoin([
      row.ssid ? `SSID ${row.ssid}` : wifiStatus.ssid ? `SSID ${wifiStatus.ssid}` : "",
      row.ipAddress || wifiStatus.ipAddress ? `IP ${row.ipAddress || wifiStatus.ipAddress}` : "",
      getSignalText(row.signalStrength ?? wifiStatus.signalLevel),
      row.macAddress || wifiStatus.macAddress ? `MAC ${row.macAddress || wifiStatus.macAddress}` : "",
    ]);
  }

  const addressMode =
    row.addressMode || getAddressModeForInterface(cfg, key);
  const macAddress =
    row.macAddress || getMacForInterface(cfg, key, wifiStatus);
  const gateway = row.gateway || getGatewayForInterface(cfg, key, wifiStatus);
  const bridgeMode =
    key === "eth1" && cfg.secondaryNetworkInterfaceMode === "bridge_mode"
      ? "Bridge member"
      : "";
  const healthMonitoring =
    key === "eth1" &&
    cfg.secondaryNetworkInterfaceMode === "secondary_nic" &&
    !getMonitoringEnabled(networkStatus)
      ? "Not health monitored"
      : "";

  return compactJoin([
    failoverDetail,
    row.ipAddress ? `IP ${row.ipAddress}` : "",
    addressMode,
    macAddress ? `MAC ${macAddress}` : "",
    gateway ? `Gateway ${gateway}` : "",
    bridgeMode,
    healthMonitoring,
    routePriority,
    lastCheck,
  ]);
};

const shouldDisplayInterfaceRow = (row = {}, networkStatus = null, conf = {}) => {
  const interfaceName = normalizeInterfaceKey(row.interfaceName);
  if (interfaceName !== "eth1") return true;
  if (isSecondaryInterfaceModeEnabled(conf)) return true;
  return Boolean(row.wanHealth || findWanRouteForInterface("eth1", networkStatus));
};

const getInterfaceSortOrder = (row = {}) => {
  const key = normalizeInterfaceKey(row.interfaceName);
  if (row.active) return 0;
  if (key === "eth0") return 10;
  if (key === "lte0") return 20;
  if (key === "wlan0") return 30;
  if (key === "eth1") return 40;
  if (isVlanInterface(key)) return 50;
  return 60;
};

const sortInterfaceRows = (rows = []) =>
  [...rows].sort((left, right) => getInterfaceSortOrder(left) - getInterfaceSortOrder(right));

export const getActiveUplinkSummaryFromConf = (
  conf = {},
  networkStatus = null,
) => {
  const cfg = safeConf(conf);
  const active = networkStatus?.activeUplink;
  const activeRoute = getActiveRoute(cfg, networkStatus);
  const lteEnabled = isCellularEnabled(cfg);
  const ltePolicy = cfg.ltePolicy || "";
  if (
    active?.label &&
    (!activeRoute ||
      interfacesMatch(active.interfaceName, activeRoute) ||
      interfacesMatch(active.runtimeInterfaceName, activeRoute))
  ) {
    const interfaceName = normalizeInterfaceKey(activeRoute || active.interfaceName);
    const isLte = isLteInterface(interfaceName);
    const failoverDetail = getFailoverDetail(cfg, interfaceName, true);
    let label = isFrontEthernetInterface(interfaceName)
      ? formatInterfaceLabel(interfaceName)
      : active.label;

    if (lteEnabled && ltePolicy === "lteFallback" && isLte) {
      label = "LTE fallback active";
    } else if (
      lteEnabled &&
      ltePolicy === "lteOnly" &&
      isFrontEthernetInterface(interfaceName)
    ) {
      label = "Ethernet fallback active";
    }

    const row = (networkStatus?.interfaces || []).find((item) =>
      interfacesMatch(item.interfaceName, interfaceName),
    );
    const route = row?.wanHealth || findWanRouteForInterface(interfaceName, networkStatus);
    const enrichedDetail = row
      ? getInterfaceDetail({
          row: {
            ...row,
            interfaceName,
            ipAddress: isLte ? "" : active.ipAddress || row.ipAddress || "",
          },
          conf: cfg,
          networkStatus,
          route,
          active: true,
        })
      : "";
    const detail =
      failoverDetail || enrichedDetail || active.statusDetail || row?.statusDetail || "";

    return {
      interfaceName,
      label,
      ipAddress: isLte ? "" : active.ipAddress || row?.ipAddress || "",
      detail,
      isLte,
    };
  }

  const configuredRoute = getConfiguredDefaultRoute(cfg);
  const route = configuredRoute || activeRoute || resolveWanPrimaryInterface(cfg);
  const interfaceName = normalizeInterfaceKey(route);
  const isLte = isLteInterface(interfaceName);
  const ipAddress = isLte
    ? ""
    : getIpForInterface(cfg, interfaceName, networkStatus?.wifi?.data || {});
  let label = formatInterfaceLabel(interfaceName);
  let detail = "";

  if (lteEnabled && ltePolicy === "lteFallback") {
    if (isLte) {
      label = "LTE fallback active";
      detail = "Ethernet primary not active";
    } else if (isFrontEthernetInterface(interfaceName)) {
      detail = "LTE fallback enabled";
    }
  } else if (lteEnabled && ltePolicy === "lteOnly") {
    if (isLte) {
      label = "LTE primary";
      detail = "Ethernet fallback";
    } else if (isFrontEthernetInterface(interfaceName)) {
      label = "Ethernet fallback active";
      detail = "LTE primary not active";
    }
  } else if (isLte) {
    detail = "Cellular uplink";
  }

  return {
    interfaceName,
    label,
    ipAddress,
    detail,
    isLte,
  };
};

export const buildFallbackInterfaceRows = (conf = {}) => {
  const cfg = safeConf(conf);
  const wanPrimary = resolveWanPrimaryInterface(cfg);
  const primaryEthernet = resolvePrimaryEthernetInterface(cfg);
  const rows = [
    {
      interfaceName: normalizeInterfaceKey(primaryEthernet),
      label: formatInterfaceLabel(primaryEthernet),
      role: normalizeInterfaceKey(wanPrimary) === "lte0" ? "Fallback" : "Primary",
      health: cfg.primaryInterfaceStatus || (getPrimaryIp(cfg) ? "Up" : "Unknown"),
      linkSignal: cfg.primaryInterfaceStatus || "",
      statusDetail: getPrimaryIp(cfg) ? `IP ${getPrimaryIp(cfg)}` : "",
      ipAddress: getPrimaryIp(cfg),
      active: interfacesMatch(cfg.currentDefaultRoute || wanPrimary, primaryEthernet),
    },
  ];

  const secondaryInterface = resolveSecondaryInterface(cfg);
  if (secondaryInterface) {
    rows.push({
      interfaceName: "eth1",
      label: formatInterfaceLabel("eth1"),
      role: getSecondaryInterfaceRole(cfg),
      health: cfg.secondInterfaceStatus || (getSecondaryIp(cfg) ? "Up" : "Unknown"),
      linkSignal: cfg.secondInterfaceStatus || "",
      statusDetail: getSecondaryIp(cfg) ? `IP ${getSecondaryIp(cfg)}` : "",
      ipAddress: getSecondaryIp(cfg),
      active: interfacesMatch(cfg.currentDefaultRoute, "eth1"),
    });
  }

  if (hasCellularConfig(cfg)) {
    rows.push({
      interfaceName: "lte0",
      runtimeInterfaceName: "ppp0",
      label: "LTE (lte0)",
      role: cfg.ltePolicy === "lteOnly" ? "Primary" : "Fallback",
      health: isCellularEnabled(cfg) ? "Enabled" : "Disabled",
      linkSignal: "",
      statusDetail: cfg.ltePolicy || "",
      ipAddress: "",
      active: interfacesMatch(cfg.currentDefaultRoute || wanPrimary, "lte0"),
    });
  }

  return rows;
};

export const buildInterfaceRows = (networkStatus = null, conf = {}) => {
  const cfg = safeConf(conf);
  const enhanceRow = (item) => {
    const interfaceName = normalizeInterfaceKey(item.interfaceName);
    const route = item.wanHealth || findWanRouteForInterface(interfaceName, networkStatus);
    const active = isRowActive({ ...item, interfaceName }, cfg, networkStatus);
    const row = {
      ...item,
      interfaceName,
      label: interfaceName
        ? formatInterfaceLabel(interfaceName)
        : item.label || "Unknown",
      role:
        interfaceName === "eth1"
          ? getSecondaryInterfaceRole(cfg)
          : item.role || "",
      ipAddress: isLteInterface(interfaceName)
        ? ""
        : item.ipAddress ||
          getIpForInterface(cfg, interfaceName, networkStatus?.wifi?.data || {}),
      active,
      wanHealth: route || item.wanHealth || null,
    };
    const health = getConnectivityStatus({
      row,
      conf: cfg,
      networkStatus,
      route,
      active,
    });
    const statusDetail = getInterfaceDetail({
      row,
      conf: cfg,
      networkStatus,
      route,
      active,
    });
    return {
      ...row,
      health,
      statusDetail: statusDetail || row.statusDetail || row.linkSignal || "",
    };
  };

  if (Array.isArray(networkStatus?.interfaces) && networkStatus.interfaces.length) {
    const rows = networkStatus.interfaces
      .map(enhanceRow)
      .filter((row) => shouldDisplayInterfaceRow(row, networkStatus, cfg));
    const existingInterfaces = rows.map((row) => row.interfaceName);
    const configuredSecondaryRows =
      resolveSecondaryInterface(cfg) &&
      !existingInterfaces.some((item) => interfacesMatch(item, "eth1"))
        ? buildFallbackInterfaceRows(cfg)
            .filter((row) => interfacesMatch(row.interfaceName, "eth1"))
            .map(enhanceRow)
            .filter((row) => shouldDisplayInterfaceRow(row, networkStatus, cfg))
        : [];
    const interfacesWithConfiguredSecondary = [
      ...rows,
      ...configuredSecondaryRows,
    ];
    const interfaceNamesWithConfiguredSecondary =
      interfacesWithConfiguredSecondary.map((row) => row.interfaceName);
    const extraWanRows = getWanHealthRoutes(networkStatus)
      .filter((route) => {
        const interfaceName = normalizeInterfaceKey(getRouteInterface(route));
        if (!interfaceName) return false;
        return !interfaceNamesWithConfiguredSecondary.some((item) =>
          interfacesMatch(item, interfaceName),
        );
      })
      .map((route) => {
        const routeHealthy = getRouteHealthy(route);
        return enhanceRow({
          interfaceName: normalizeInterfaceKey(getRouteInterface(route)),
          label: formatInterfaceLabel(getRouteInterface(route)),
          type: isVlanInterface(getRouteInterface(route)) ? "vlan" : "wan",
          role: "WAN route",
          health:
            routeHealthy === false
              ? "Unhealthy"
              : routeHealthy === true
                ? "Healthy"
                : "Unknown",
          statusDetail: "",
          wanHealth: route,
        });
      });
    return sortInterfaceRows([
      ...interfacesWithConfiguredSecondary,
      ...extraWanRows,
    ]);
  }
  return sortInterfaceRows(
    buildFallbackInterfaceRows(cfg)
      .map(enhanceRow)
      .filter((row) => shouldDisplayInterfaceRow(row, networkStatus, cfg)),
  );
};
